import { type ColumnPinningState, type Updater } from "@tanstack/react-table";
import { useMemo, useEffect } from "react";
import { useLocalStorage } from "react-use";

const defaultPinning: ColumnPinningState = { left: [], right: [] };

export function computeInitialPinning(
  storedPinning: ColumnPinningState | undefined,
  columnOrder: string[],
): ColumnPinningState {
  if (storedPinning && Array.isArray(storedPinning.left) && storedPinning.left.length === 0) {
    return defaultPinning;
  }

  const left = storedPinning?.left ?? [];
  if (left.length > 0) {
    const pinnedColumnId = left[0];
    if (pinnedColumnId && columnOrder.includes(pinnedColumnId)) {
      return { left, right: storedPinning?.right ?? [] };
    }
  }
  const firstColumnId = columnOrder[0];
  return firstColumnId ? { left: [firstColumnId], right: [] } : defaultPinning;
}

export function computePinningUpdate(storedPinning: ColumnPinningState | undefined, columnOrder: string[]) {
  if (storedPinning && Array.isArray(storedPinning.left) && storedPinning.left.length === 0) {
    return null;
  }

  const left = storedPinning?.left ?? [];
  if (left.length > 0) {
    const currentPinnedId = left[0];
    const newFirstColumnId = columnOrder[0];

    // If the pinned column is no longer first, update to pin the new first column
    if (
      newFirstColumnId &&
      currentPinnedId &&
      currentPinnedId !== newFirstColumnId &&
      columnOrder.includes(currentPinnedId)
    ) {
      return { left: [newFirstColumnId], right: [] };
    }
  } else if (!storedPinning) {
    // If no column was pinned and we haven't explicitly unpinned (storedPinning is undefined/null), pin the first column
    const firstColumnId = columnOrder[0];
    if (firstColumnId) {
      return { left: [firstColumnId], right: [] };
    }
  }

  return null;
}

export function useTableColumnPinning(tableId: string, columnOrder: string[]) {
  const [storedPinning, setStoredPinning] = useLocalStorage<ColumnPinningState>(
    `table-pinning-${tableId}`,
    defaultPinning,
  );

  const initialPinning = useMemo(() => computeInitialPinning(storedPinning, columnOrder), [storedPinning, columnOrder]);

  useEffect(() => {
    const update = computePinningUpdate(storedPinning, columnOrder);
    if (update) {
      setStoredPinning(update);
    }
  }, [columnOrder, storedPinning, setStoredPinning]);

  const onColumnPinningChange = (updater: Updater<ColumnPinningState>) => {
    const updatedPinning = typeof updater === "function" ? updater(storedPinning ?? defaultPinning) : updater;
    setStoredPinning(updatedPinning);
  };

  return {
    columnPinning: initialPinning,
    onColumnPinningChange,
  };
}
